import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { FileText } from 'lucide-react';
import {
  DISCUSSION_MENTION_ALL_TOKEN,
  type DiscussionAttachment,
  type DiscussionMessage,
} from '@allemnii-pulse/shared';
import type { UserProfile } from '../../lib/userProfiles.js';
import { cn } from '../../lib/utils.js';

/** The name a mention token is written with — "@Ana Silva". Full display name rather than the
 * "First L." used elsewhere, so the person reading the message knows exactly who's meant. */
export function mentionName(uid: string, profile?: UserProfile): string {
  return (
    profile?.displayName?.trim() ||
    profile?.email?.split('@')[0]?.trim() ||
    `User ${uid.slice(0, 6)}`
  );
}

/** A message as one line of plain text — for quotes and lists, where raw markdown (`**`, link
 * brackets) would just be noise. */
export function plainExcerpt(message: Pick<DiscussionMessage, 'body' | 'attachments'>): string {
  const text = message.body
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_~`>#]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (text) return text;
  const n = message.attachments.length;
  return n === 1 ? 'Attachment' : `${n} attachments`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * A message body as sanitized markdown, with the "@Name" tokens of its stored mentions (and
 * "@everyone") highlighted — matched as exact substrings, not re-parsed, so only tags that were
 * really sent light up, and yours more strongly. Same approach as task comments (TaskDetailPage):
 * highlighting runs on the HTML's text segments only, and sanitizing comes last.
 */
export function MessageBody({
  body,
  mentionedUids,
  mentionsAll,
  myUid,
  profiles,
}: {
  body: string;
  mentionedUids: string[];
  mentionsAll: boolean;
  myUid: string;
  profiles: Record<string, UserProfile>;
}) {
  if (!body) return null;
  let html = marked.parse(body, { async: false, breaks: true, gfm: true });

  const tokens = new Map<string, boolean>(); // escaped token -> is it me
  for (const uid of mentionedUids) {
    tokens.set(escapeHtml(`@${mentionName(uid, profiles[uid])}`), uid === myUid);
  }
  if (mentionsAll) tokens.set(DISCUSSION_MENTION_ALL_TOKEN, true);
  const present = [...tokens.keys()].filter((t) => html.includes(t));
  if (present.length > 0) {
    // Longest first, so "@Ana Silva" wins over a shorter "@Ana" that prefixes it.
    present.sort((a, b) => b.length - a.length);
    const pattern = new RegExp(`(${present.map(escapeRegExp).join('|')})`, 'g');
    html = html
      .split(/(<[^>]+>)/)
      .map((part) =>
        part.startsWith('<')
          ? part
          : part.replace(pattern, (token) =>
              tokens.get(token)
                ? `<span class="rounded bg-accent-soft px-0.5 font-medium text-accent-soft-fg">${token}</span>`
                : `<span class="font-medium text-accent">${token}</span>`,
            ),
      )
      .join('');
  }

  return (
    <div
      className="doc-prose max-w-none break-words text-[15px] leading-relaxed md:text-sm [&_a]:break-all"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Images as a thumbnail grid that opens full size; documents as tappable file chips. Both open
 * the signed URL in a new tab, where the phone's own viewer handles PDFs and Word files. */
export function Attachments({
  attachments,
  urls,
}: {
  attachments: DiscussionAttachment[];
  urls: Record<string, string>;
}) {
  if (attachments.length === 0) return null;
  const images = attachments.filter((a) => a.kind === 'image');
  const documents = attachments.filter((a) => a.kind !== 'image');

  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      {images.length > 0 && (
        <div
          className={cn(
            'grid max-w-sm gap-1.5',
            images.length === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3',
          )}
        >
          {images.map((image) => {
            const url = urls[image.id];
            return (
              <a
                key={image.id}
                href={url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${image.name}`}
                className={cn(
                  'block overflow-hidden rounded-lg border border-border bg-surface-2',
                  images.length === 1 ? 'max-h-72' : 'aspect-square',
                )}
              >
                {url ? (
                  <img
                    src={url}
                    alt={image.name}
                    loading="lazy"
                    className={cn(
                      'h-full w-full',
                      images.length === 1 ? 'max-h-72 object-contain' : 'object-cover',
                    )}
                  />
                ) : (
                  <div className="aspect-video w-full animate-pulse" />
                )}
              </a>
            );
          })}
        </div>
      )}
      {documents.map((file) => (
        <a
          key={file.id}
          href={urls[file.id]}
          target="_blank"
          rel="noreferrer"
          className="flex max-w-sm items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2 text-text no-underline hover:bg-surface-2"
        >
          <FileText className="h-5 w-5 shrink-0 text-accent" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{file.name}</span>
            <span className="block text-xs text-muted">
              {file.name.split('.').pop()?.toUpperCase()} · {formatSize(file.sizeBytes)}
            </span>
          </span>
        </a>
      ))}
    </div>
  );
}
